/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { UserPreferencesService } from '@alfresco/adf-core';
import { forkJoin, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UserPreferenceService } from './user-preference.service';

const PREF_PAGE_SIZE = 'aca.settings.pageSize';
const PREF_LANDING = 'aca.settings.landing';

/** Retired landing values and the route that replaced them. */
const RENAMED_LANDINGS: Record<string, string> = { 'my-tasks': 'my-work' };

/**
 * The display preferences chosen in Settings, kept on the server so they
 * follow the person to every machine.
 *
 * The page size is handed to ADF's own `paginationSize`, which every stock
 * list and the custom tables already start from; the landing page is where
 * the sign-in screen sends someone who did not arrive through a deep link.
 */
@Injectable({ providedIn: 'root' })
export class DisplayPreferencesService {
  private readonly preferences = inject(UserPreferenceService);
  private readonly adfPreferences = inject(UserPreferencesService);

  static readonly PAGE_SIZES = [20, 50, 100];
  static readonly DEFAULT_LANDING = 'dashboard';
  static readonly LANDINGS = [
    { value: 'dashboard', label: 'DMS.NAV.HOME' },
    { value: 'all-documents', label: 'DMS.UI.D51E1F' },
    { value: 'my-work', label: 'DMS.UI.E78A57' },
    { value: 'my-favorites', label: 'DMS.UI.AA4B66' },
    { value: 'document-search', label: 'DMS.UI.272CB7' }
  ];

  /** Reads both preferences and applies the page size; emits what was found. */
  load(): Observable<{ pageSize: number; landing: string }> {
    return forkJoin({
      pageSize: this.preferences.read(PREF_PAGE_SIZE).pipe(map((value) => DisplayPreferencesService.toPageSize(value))),
      landing: this.preferences.read(PREF_LANDING).pipe(map((value) => DisplayPreferencesService.toLanding(value)))
    }).pipe(
      tap(({ pageSize }) => {
        if (pageSize) {
          this.adfPreferences.paginationSize = pageSize;
        }
      }),
      map(({ pageSize, landing }) => ({
        pageSize: pageSize ?? this.adfPreferences.paginationSize,
        landing
      }))
    );
  }

  /** Applies the stored page size for this session — the shell calls it once. */
  apply(): void {
    // the reads already fall back to "nothing saved", so there is no error to show
    this.load().subscribe({ error: () => undefined });
  }

  savePageSize(value: number): Observable<void> {
    this.adfPreferences.paginationSize = value;
    return this.preferences.write(PREF_PAGE_SIZE, String(value));
  }

  saveLanding(value: string): Observable<void> {
    return this.preferences.write(PREF_LANDING, value);
  }

  private static toPageSize(value: string | undefined): number | undefined {
    const parsed = Number(value);
    return DisplayPreferencesService.PAGE_SIZES.includes(parsed) ? parsed : undefined;
  }

  private static toLanding(value: string | undefined): string {
    const route = value ? (RENAMED_LANDINGS[value] ?? value) : '';
    return DisplayPreferencesService.LANDINGS.some((option) => option.value === route) ? route : DisplayPreferencesService.DEFAULT_LANDING;
  }
}
